import React from "react";

interface ScopeLabelProps {
	label: string | null | undefined;
	/** Shown when there is no label at all. */
	fallback?: string;
	/**
	 * Cut to {@link MAX_LENGTH} characters. Turn this off where the surface has
	 * room for the whole name — the scoped-assignment list in the access modal
	 * is a full-width list, not a table cell, so shortening there hides text for
	 * no reason.
	 */
	truncate?: boolean;
	className?: string;
}

/**
 * PRO-731 (2.7) — a portfolio / project / RFx name, truncated for layout.
 *
 * Scope names are free text and routinely long enough to blow out a table
 * column or a pill, so by default they are cut to {@link MAX_LENGTH} characters
 * with the full value on hover; pass `truncate={false}` where the surface is
 * not space-constrained. Shared rather than repeated at each call site: the same
 * label is rendered in the assignment table, the assign drawer, the role pills
 * and the access matrix, and they were previously each formatting it their own
 * way.
 *
 * Truncation is done in JS rather than with CSS `text-overflow` because the
 * requirement is a fixed character count regardless of column width, and
 * because `title` needs the untruncated string either way.
 */
export const MAX_LENGTH = 20;

const ELLIPSIS = "…";

export const truncateScopeLabel = (value: string): string =>
	value.length > MAX_LENGTH ? `${value.slice(0, MAX_LENGTH)}${ELLIPSIS}` : value;

export const ScopeLabel = ({
	label,
	fallback = "—",
	truncate = true,
	className = "",
}: ScopeLabelProps) => {
	if (!label) {
		return <span className={className}>{fallback}</span>;
	}

	const shortened = truncate ? truncateScopeLabel(label) : label;
	const isTruncated = shortened !== label;

	return (
		<span
			className={className}
			// Only set `title` when something is actually hidden — a tooltip
			// repeating text already fully visible is noise.
			title={isTruncated ? label : undefined}
			data-testid="scope-label"
		>
			{shortened}
		</span>
	);
};
